import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CalendarIntegration } from 'src/calendar/components/SyncCalendarPage/types';
import Notification from 'src/common/components/Notification';
import useBoats from 'src/common/hooks/useBoats';
import apiFetch from 'src/pubsub/apiFetch';
import { FullBoatDetail } from 'src/types/boat/BoatDetail';

type UseSyncCalendar = {
  loading: boolean;
  integrations: CalendarIntegration[];
  syncedBoats: FullBoatDetail[];
  unsyncedBoats: FullBoatDetail[];
  getIntegrations: () => Promise<void>;
  addIntegration: (boatId: string) => Promise<void>;
  removeIntegration: (boatId: string) => Promise<void>;
  syncIntegration: (boatId: string) => Promise<void>;
  getAuthenticationStatus: () => Promise<boolean>;
};

const useSyncCalendar = (): UseSyncCalendar => {
  const [loading, setLoading] = useState<boolean>(false);
  const [integrations, setIntegrations] = useState<CalendarIntegration[]>([]);
  const { boats } = useBoats();

  const integrationIds = useMemo(
    () => integrations?.map((integration) => integration.boat) ?? [],
    [integrations],
  );

  const { synced: syncedBoats, unsynced: unsyncedBoats } = useMemo(() => {
    const synced = boats?.filter((boat) => integrationIds.includes(boat.id)) ?? [];
    const unsynced = boats?.filter((boat) => !integrationIds.includes(boat.id)) ?? [];

    return { synced, unsynced };
  }, [boats, integrationIds]);

  const getBoatHeadline = useCallback((boatId: string) => {
    const foundBoat = boats.find((boat) => boat.id === boatId);
    return foundBoat?.headline ?? '';
  }, [boats]);

  const showNotification = useCallback(
    ({ title, message }: { title: string; message?: string }) => {
      toast.dismiss();
      toast(
        <Notification
          heading={title}
          message={message}
        />,
        {
          autoClose: 5000,
        },
      );
    },
    [],
  );
  const callApi = useCallback(async (path: string = '', options = {}) => {
    const response = await apiFetch(`/calendar/integrations/${path}`, options);
    if (response.ok) {
      try {
        const result = await response.json();
        return result;
      } catch (e) {
        // Empty body
        return true;
      }
    }
    return null;
  }, []);

  const getAuthenticationStatus = useCallback(async (): Promise<boolean> => {
    const response: { valid: boolean } = await callApi('authentication_status/');
    return response?.valid || false;
  }, [callApi]);

  const getIntegrations = useCallback(async () => {
    const response = await callApi();
    if (response) {
      setIntegrations(response.results);
    } else {
      setIntegrations([]);
    }
    setLoading(true);
  }, [callApi]);

  const addIntegration = useCallback(async (boatId: string) => {
    const response = await callApi('', {
      method: 'POST',
      body: JSON.stringify({
        boat: boatId,
      }),
    });
    if (response) {
      await getIntegrations();
      showNotification({
        title: 'Calendar Linked Successfully!',
        message: 'All future bookings and events will now be automatically added for your Google Calendar to your Getmyboat calendar',
      });
    } else {
      showNotification({ title: 'Failed to link calendar. Please try again.' });
    }
  }, [callApi, getIntegrations, showNotification]);

  const removeIntegration = useCallback(async (boatId: string) => {
    const integration = integrations.find(({ boat }) => boat === boatId);
    if (!integration) {
      return;
    }
    const boatHeadline = getBoatHeadline(boatId);
    const response = await callApi(`${integration.id}/`, {
      method: 'DELETE',
    });
    if (response) {
      await getIntegrations();
      showNotification({ title: `Deleted calendar for ${boatHeadline}` });
    } else {
      showNotification({ title: `Failed to delete calendar for ${boatHeadline}` });
    }
  }, [callApi, getBoatHeadline, getIntegrations, integrations, showNotification]);

  const syncIntegration = useCallback(async (boatId: string) => {
    const integration = integrations.find(({ boat }) => boat === boatId);
    if (!integration) {
      return;
    }
    const response = await callApi(`${integration.id}/sync/`, {});
    if (response) {
      await getIntegrations();
      showNotification({ title: 'Synchronized calendar' });
    } else {
      showNotification({ title: 'Failed to sync calendar' });
    }
  }, [callApi, getIntegrations, integrations, showNotification]);

  return {
    loading: boats.length ? loading : true,
    integrations,
    syncedBoats,
    unsyncedBoats,
    getIntegrations,
    addIntegration,
    removeIntegration,
    syncIntegration,
    getAuthenticationStatus,
  };
};

export default useSyncCalendar;
